import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Edit, Download, Copy, Trash, FileText, Info, Eye, Code, Settings, Calendar, Clock, BarChart3, AlertTriangle, History, RotateCcw, Loader2, Share2 } from 'lucide-react'
import { Link } from 'react-router-dom'

// API base URL for direct downloads
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5018/api'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { DataTable, type ColumnDef, useDataTable } from '@/components/ui/data-table'
import { templateService, type TemplateOData } from '@/lib/api/services/templates'
import { resolveApiFileUrl } from '@/lib/api/client'
import { templatePermissionService } from '@/lib/api/services/templatePermissions'
import type { TemplateVersion, PermissionLevel } from '@/lib/api/types'
import { useToast } from '@/components/ui/use-toast'
import { PermissionGate } from '@/components/auth/permission-gate'
import { FluentCard } from '@/components/layout/page-header'
import { TemplateSharingPanel, ShareDialog } from '@/components/templates/sharing'
import { useAuth } from '@/lib/contexts/auth-context'

export default function TemplateDetailPage() {
  const params = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [template, setTemplate] = useState<TemplateOData | null>(null)
  const [loading, setLoading] = useState(true)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [versions, setVersions] = useState<TemplateVersion[]>([])
  const [versionsLoading, setVersionsLoading] = useState(false)
  const [versionsError, setVersionsError] = useState<string | null>(null)
  const [restoreLoading, setRestoreLoading] = useState<string | null>(null)
  const [effectivePermission, setEffectivePermission] = useState<PermissionLevel>('View')
  const [showSharingPanel, setShowSharingPanel] = useState(false)
  const [shareDialogOpen, setShareDialogOpen] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const { user } = useAuth()

  const handleDownload = async () => {
    if (!template) return

    setDownloading(true)
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch(`${API_BASE_URL}/v1/templates/${template.id}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })

      if (!response.ok) {
        throw new Error('Download failed')
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition')
      let filename = template.originalFileName || `${template.name}.${template.type === 'Word' ? 'docx' : template.type === 'Excel' ? 'xlsx' : template.type === 'PowerPoint' ? 'pptx' : 'file'}`
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, '')
        }
      }

      // Create blob and download
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      toast({
        title: 'Success',
        description: 'Template downloaded successfully',
      })
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to download template',
        variant: 'destructive',
      })
    } finally {
      setDownloading(false)
    }
  }

  const {
    page,
    pageSize,
    sortField,
    sortDirection,
    filters,
    search,
    setPage,
    setPageSize,
    setSort,
    setFilters,
    setSearch,
  } = useDataTable({
    initialState: {
      pageSize: 10,
      sortField: 'versionNumber',
      sortDirection: 'desc',
    },
  })

  useEffect(() => {
    if (params.id) {
      loadTemplate(params.id as string)
    }
  }, [params.id])

  const loadTemplate = async (id: string) => {
    try {
      setLoading(true)
      const [data, effectivePerm] = await Promise.all([
        templateService.getTemplate(id),
        templatePermissionService.getEffectivePermission(id).catch(() => ({ permissionLevel: 'View' as PermissionLevel })),
      ])
      setTemplate(data)
      setEffectivePermission(effectivePerm.permissionLevel)
      // Load versions only after template is successfully loaded
      loadVersions(id)
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Template not found',
        variant: 'destructive',
      })
      navigate('/templates')
    } finally {
      setLoading(false)
    }
  }

  const loadVersions = useCallback(async (id: string) => {
    try {
      setVersionsLoading(true)
      setVersionsError(null)
      const data = await templateService.getTemplateVersions(id)
      setVersions(data || [])
    } catch (_error) {
      console.error('Failed to load versions', _error)
      setVersionsError('Failed to load version history')
      setVersions([])
    } finally {
      setVersionsLoading(false)
    }
  }, [])

  const handleRestoreVersion = async (versionId: string) => {
    if (!template) return

    setRestoreLoading(versionId)
    try {
      await templateService.restoreVersion(template.id, versionId)
      toast({
        title: 'Success',
        description: 'Template version restored successfully',
      })
      // Reload both template and versions
      await loadTemplate(template.id)
      await loadVersions(template.id)
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to restore template version',
        variant: 'destructive',
      })
    } finally {
      setRestoreLoading(null)
    }
  }

  // Filter and sort versions based on DataTable state
  const filteredVersions = versions
    .filter((version) => {
      // Search filter
      if (search) {
        const searchLower = search.toLowerCase()
        return (
          version.versionNumber.toString().includes(searchLower) ||
          version.status?.toLowerCase().includes(searchLower) ||
          version.changeDescription?.toLowerCase().includes(searchLower) ||
          version.originalFileName?.toLowerCase().includes(searchLower)
        )
      }
      return true
    })
    .filter((version) => {
      // Column filters
      const statusFilter = filters.status?.value
      if (statusFilter && version.status !== statusFilter) return false
      return true
    })
    .sort((a, b) => {
      // Sorting
      const aValue = a[sortField as keyof TemplateVersion]
      const bValue = b[sortField as keyof TemplateVersion]
      if (aValue === undefined || bValue === undefined) return 0
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })

  // Paginate
  const paginatedVersions = filteredVersions.slice((page - 1) * pageSize, page * pageSize)

  const versionColumns: ColumnDef<TemplateVersion>[] = [
    {
      id: 'versionNumber',
      header: 'Version',
      accessor: 'versionNumber',
      sortable: true,
      width: '100px',
      cell: (version) => {
        const isCurrentVersion = version.versionNumber === template?.currentVersionNumber
        return (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
              <History className="h-3 w-3" />
              v{version.versionNumber}
            </span>
            {isCurrentVersion && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-800 dark:text-green-300">
                Current
              </span>
            )}
          </div>
        )
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'status',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Draft', value: 'Draft' },
        { label: 'Published', value: 'Published' },
        { label: 'Retired', value: 'Retired' },
      ],
      cell: (version) => (
        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
          version.status === 'Published'
            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            : version.status === 'Retired'
            ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
        }`}>
          {version.status || 'Draft'}
        </span>
      ),
    },
    {
      id: 'changeDescription',
      header: 'Change Description',
      accessor: 'changeDescription',
      sortable: true,
      cell: (version) => (
        <span className="text-gray-700 dark:text-gray-300 text-sm">
          {version.changeDescription || '—'}
        </span>
      ),
    },
    {
      id: 'originalFileName',
      header: 'File Name',
      accessor: 'originalFileName',
      sortable: true,
      cell: (version) => (
        <span className="text-gray-500 dark:text-gray-400 text-sm truncate max-w-[200px] block">
          {version.originalFileName || '—'}
        </span>
      ),
    },
    {
      id: 'createdAt',
      header: 'Created',
      accessor: 'createdAt',
      sortable: true,
      cell: (version) => (
        <span className="text-gray-500 dark:text-gray-400 text-sm">
          {new Date(version.createdAt).toLocaleString()}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: 'id',
      width: '120px',
      cell: (version) => {
        const isCurrentVersion = version.versionNumber === template?.currentVersionNumber
        if (isCurrentVersion) {
          return <span className="text-gray-400 text-xs">Current version</span>
        }
        return (
          <PermissionGate permissions={['templates.update']}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleRestoreVersion(version.id)}
              disabled={restoreLoading === version.id}
              className="rounded-lg gap-2"
            >
              {restoreLoading === version.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <RotateCcw className="h-4 w-4" />
                  Restore
                </>
              )}
            </Button>
          </PermissionGate>
        )
      },
    },
  ]

  const handleDelete = async () => {
    if (!template) return

    setDeleteLoading(true)
    try {
      await templateService.deleteTemplate(template.id)
      toast({
        title: 'Success',
        description: 'Template moved to bin',
      })
      setDeleteDialogOpen(false)
      navigate('/templates')
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to delete template',
        variant: 'destructive',
      })
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleDuplicate = async () => {
    if (!template) return

    try {
      // Create a copy of the template
      const copy = {
        ...template,
        name: `${template.name} (Copy)`,
        isActive: false,
      }
      const newTemplate = await templateService.createTemplate(copy)
      toast({
        title: 'Success',
        description: 'Template duplicated successfully',
      })
      navigate(`/templates/${newTemplate.id}/edit`)
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to duplicate template',
        variant: 'destructive',
      })
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-5 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-8 w-64 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
            <div className="h-4 w-96 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
          </div>
          <div className="flex gap-2">
            <div className="h-10 w-24 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
            <div className="h-10 w-20 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
          </div>
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          <FluentCard className="lg:col-span-2">
            <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
          </FluentCard>
          <FluentCard>
            <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
          </FluentCard>
        </div>
      </div>
    )
  }

  if (!template) return null

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Link
        to="/templates"
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Templates
      </Link>

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-2xl bg-gradient-to-br from-blue-400 to-blue-600 shadow-lg shadow-blue-500/25">
            <FileText className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{template.name}</h1>
            <p className="text-gray-500 dark:text-gray-400">
              {template.description || 'No description provided'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShareDialogOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-full transition-colors"
          >
            <Share2 className="h-4 w-4" />
            Share
          </button>
          <button
            onClick={() => setShowSharingPanel(!showSharingPanel)}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-colors ${
              showSharingPanel
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                : 'text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            Permissions
          </button>
          <Link
            to={`/templates/${template.id}/analytics`}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <BarChart3 className="h-4 w-4" />
            Analytics
          </Link>
          <button
            onClick={handleDuplicate}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <Copy className="h-4 w-4" />
            Duplicate
          </button>
          <PermissionGate permissions={['templates.update']}>
            <Link
              to={`/templates/${template.id}/edit`}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <Edit className="h-4 w-4" />
              Edit
            </Link>
          </PermissionGate>
          <PermissionGate permissions={['templates.delete']}>
            <button
              onClick={() => setDeleteDialogOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-full transition-colors"
            >
              <Trash className="h-4 w-4" />
              Delete
            </button>
          </PermissionGate>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <FluentCard className="lg:col-span-2">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-400 to-purple-600">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Template Details</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Preview and content</p>
            </div>
          </div>

          <Tabs defaultValue="preview" className="w-full">
            <TabsList className="inline-flex h-11 items-center justify-start gap-1 rounded-xl bg-gray-100 dark:bg-gray-800 p-1 w-full sm:w-auto">
              <TabsTrigger
                value="preview"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-all data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm dark:data-[state=active]:bg-gray-700 dark:data-[state=active]:text-white"
              >
                <Eye className="h-4 w-4" />
                Preview
              </TabsTrigger>
              <TabsTrigger
                value="content"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-all data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm dark:data-[state=active]:bg-gray-700 dark:data-[state=active]:text-white"
              >
                <Code className="h-4 w-4" />
                Content
              </TabsTrigger>
              <TabsTrigger
                value="settings"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-all data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm dark:data-[state=active]:bg-gray-700 dark:data-[state=active]:text-white"
              >
                <Settings className="h-4 w-4" />
                Settings
              </TabsTrigger>
            </TabsList>

            <TabsContent value="preview" className="mt-6">
              <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-6 min-h-[200px]">
                {template.thumbnailUrl ? (
                  <div className="flex flex-col items-center">
                    <img
                      src={resolveApiFileUrl(template.thumbnailUrl)}
                      alt={`Preview of ${template.name}`}
                      className="max-w-full h-auto rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg"
                    />
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                      Template preview
                    </p>
                  </div>
                ) : template.originalFileName || template.filePath ? (
                  <div className="flex flex-col items-center justify-center text-center py-8">
                    <div className={`p-6 rounded-2xl mb-4 ${
                      template.type === 'Word' ? 'bg-blue-100 dark:bg-blue-900/30' :
                      template.type === 'Excel' ? 'bg-green-100 dark:bg-green-900/30' :
                      template.type === 'PowerPoint' ? 'bg-orange-100 dark:bg-orange-900/30' :
                      'bg-gray-100 dark:bg-gray-700'
                    }`}>
                      <FileText className={`h-16 w-16 ${
                        template.type === 'Word' ? 'text-blue-600 dark:text-blue-400' :
                        template.type === 'Excel' ? 'text-green-600 dark:text-green-400' :
                        template.type === 'PowerPoint' ? 'text-orange-600 dark:text-orange-400' :
                        'text-gray-400'
                      }`} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                      {template.originalFileName || template.name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                      {template.type} Template
                      {template.fileSize && ` • ${(template.fileSize / 1024).toFixed(1)} KB`}
                    </p>
                    <button
                      onClick={handleDownload}
                      disabled={downloading}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-xl transition-colors"
                    >
                      {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                      {downloading ? 'Downloading...' : 'Download to Preview'}
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center text-center py-8">
                    <div className="p-4 rounded-2xl bg-gray-100 dark:bg-gray-700 mb-4">
                      <Eye className="h-8 w-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 dark:text-gray-400">
                      No preview available for this template
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      Upload a template file to see preview
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="content" className="mt-6">
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">File Information</h3>
                <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-6">
                  {template.originalFileName || template.filePath ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-4">
                        <div className="p-3 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600">
                          <FileText className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 dark:text-white">
                            {template.originalFileName || 'Template File'}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {template.type} document
                            {template.fileSize && ` • ${(template.fileSize / 1024).toFixed(1)} KB`}
                          </p>
                        </div>
                        <button
                          onClick={handleDownload}
                          disabled={downloading}
                          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 rounded-xl transition-colors"
                        >
                          {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                          {downloading ? 'Downloading...' : 'Download'}
                        </button>
                      </div>
                      {template.filePath && (
                        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                            Storage Path
                          </p>
                          <code className="text-sm text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                            {template.filePath}
                          </code>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center text-center py-4">
                      <FileText className="h-8 w-8 text-gray-400 mb-2" />
                      <p className="text-gray-500 dark:text-gray-400">
                        No file content available
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="settings" className="mt-6">
              <div className="space-y-6">
                {/* Template Settings */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Template Settings</h3>
                  <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4 space-y-4">
                    <div className="flex items-center justify-between p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                          <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">Category</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Template categorization</p>
                        </div>
                      </div>
                      <span className="text-sm px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                        {template.category || 'Uncategorized'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${template.isActive ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
                          <Eye className={`h-4 w-4 ${template.isActive ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">Status</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Template visibility</p>
                        </div>
                      </div>
                      <span className={`text-sm px-3 py-1 rounded-full ${
                        template.isActive
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                      }`}>
                        {template.isActive ? 'Active' : 'Draft'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                          <History className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">Version</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Current template version</p>
                        </div>
                      </div>
                      <span className="text-sm px-3 py-1 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                        v{template.currentVersionNumber || 1}
                      </span>
                    </div>
                    {template.tags && template.tags.length > 0 && (
                      <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                            <Settings className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">Tags</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 ml-11">
                          {template.tags.map((tag, idx) => (
                            <span key={idx} className="text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Variables Configuration */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Variables Configuration</h3>
                  <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4">
                    {template.variables && template.variables.length > 0 ? (
                      <div className="space-y-3">
                        {template.variables.map((variable, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
                          >
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-lg bg-violet-100 dark:bg-violet-900/30">
                                <Code className="h-4 w-4 text-violet-600 dark:text-violet-400" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {variable.displayName || variable.name}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {variable.name} • {variable.type}
                                  {variable.required && ' • Required'}
                                </p>
                              </div>
                            </div>
                            {variable.defaultValue !== undefined && (
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                Default: {String(variable.defaultValue)}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center text-center py-6">
                        <Code className="h-6 w-6 text-gray-400 mb-2" />
                        <p className="text-gray-500 dark:text-gray-400 text-sm">
                          No variables configured
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          Variables can be added when editing the template
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </FluentCard>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Information */}
          <FluentCard>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-400 to-green-600">
                <Info className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Information</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Template metadata</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                  Type
                </p>
                <span className="inline-flex px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                  {template.type}
                </span>
              </div>

              <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                  Status
                </p>
                <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                  template.isActive
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                }`}>
                  {template.isActive ? 'Active' : 'Draft'}
                </span>
              </div>

              <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="h-3.5 w-3.5 text-gray-400" />
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Created
                  </p>
                </div>
                <p className="text-sm text-gray-900 dark:text-white">
                  {new Date(template.createdAt).toLocaleDateString()}
                </p>
              </div>

              <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="h-3.5 w-3.5 text-gray-400" />
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Updated
                  </p>
                </div>
                <p className="text-sm text-gray-900 dark:text-white">
                  {new Date(template.updatedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </FluentCard>

          {/* Actions */}
          <FluentCard>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-amber-400 to-orange-600">
                <Download className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Actions</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Export and share</p>
              </div>
            </div>

            {template.filePath || template.originalFileName ? (
              <button
                onClick={handleDownload}
                disabled={downloading}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-xl transition-colors"
              >
                {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {downloading ? 'Downloading...' : 'Export Template'}
              </button>
            ) : (
              <button
                disabled
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 rounded-xl cursor-not-allowed"
              >
                <Download className="h-4 w-4" />
                No file to export
              </button>
            )}
          </FluentCard>

        </div>
      </div>

      {/* Sharing Panel - Conditionally shown */}
      {showSharingPanel && (
        <TemplateSharingPanel
          templateId={template.id}
          templateName={template.name}
          effectivePermission={effectivePermission}
        />
      )}

      {/* Version History - Full Width Section */}
      <FluentCard>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600">
            <History className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Version History</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Current version: v{template.currentVersionNumber || 1} • {versions.length} version(s) total
            </p>
          </div>
        </div>

        <DataTable
          columns={versionColumns}
          data={paginatedVersions}
          totalCount={filteredVersions.length}
          loading={versionsLoading}
          error={versionsError}
          onRetry={() => params.id && loadVersions(params.id)}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          sortField={sortField}
          sortDirection={sortDirection}
          onSortChange={setSort}
          filters={filters}
          onFilterChange={setFilters}
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search versions..."
          rowKey="id"
          emptyMessage="No versions found"
          pageSizeOptions={[5, 10, 20]}
        />
      </FluentCard>

      {/* Share Dialog */}
      <ShareDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        templateId={template.id}
        templateName={template.name}
        currentUserId={user?.id}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Delete Template
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{template?.name}"?
              This template will be moved to the bin where you can restore it if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              onClick={handleDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
